'use client';

import { memo, useCallback, useMemo, useState } from 'react';
import styles from "./FaqUIWithoutCategory.module.scss"
import cn from "clsx";
import ArrowSVG from '@/SvgIcons/ArrowDown';

export interface StrapiFaqItem {
  id?: string;
  question?: string | null;
  answer?: string | null;
  type?: string | null;
}

const FaqAnswer = ({ faq }: { faq: StrapiFaqItem }) => (
  <div
    className={cn("!text-[#1A1A1A]-600", styles.faqAns, styles.faqAnsCk)}
    dangerouslySetInnerHTML={{ __html: faq.answer ?? "" }}
  />
);

interface FaqItemProps {
  faq: StrapiFaqItem;
  index: number;
  isOpen: boolean;
  onToggle: (index: number) => void;
}

// Memoized so a click only re-renders the item being opened and the one being
// closed, instead of the entire FAQ list (keeps INP low).
const FaqItem = memo(({ faq, index, isOpen, onToggle }: FaqItemProps) => (
  <div
    className={cn(styles.faqItem, {
      [styles.faqItemOpen]: isOpen,
    })}
  >
    <div className={styles.faqQuesHeading}>
      <button
        type="button"
        className={cn(
          "flex justify-between items-center cursor-pointer py-1 w-full text-left",
          styles.faqQues
        )}
        onClick={() => onToggle(index)}
        aria-expanded={isOpen}
      >
        <h3
          className={cn("font-medium", styles.faqQue, {
            [styles.activeQuestion]: isOpen,
          })}
        >
          {faq.question}
        </h3>
        <span className={styles.icon}>
          <ArrowSVG
          id = {`faq-icon-${index}`}
            rotate={isOpen ? 180 : 0}
            width={16}
            height={16}
            isActive={isOpen}
            color="blue"
          />
        </span>
      </button>
    </div>
    <div
      className={cn(styles.answerCollapse, {
        [styles.answerOpen]: isOpen,
      })}
    >
      <div className={styles.answerCollapseInner}>
        <FaqAnswer faq={faq} />
      </div>
    </div>
  </div>
));
FaqItem.displayName = "FaqItem";

interface FaqUIWithoutCategoryProps {
  faqs?: StrapiFaqItem[] | null;
  isHome?: boolean;
  // When true, FAQs are grouped into tabs by their Strapi `type`.
  showTypes?: boolean;
}

const OTHERS_TAB = "OTHERS";

const getTabName = (type?: string | null) =>
  type ? type.replace(/_/g, " ").trim() : OTHERS_TAB;

const FaqUIWithoutCategory = ({ faqs, isHome = false, showTypes = false }: FaqUIWithoutCategoryProps) => {
  const items = useMemo(
    () => (faqs ?? []).filter((item) => item?.question && item?.answer),
    [faqs]
  );

  // Untyped FAQs go into a trailing "OTHERS" tab so none are dropped.
  const tabs = useMemo(() => {
    if (!showTypes) return [];
    const names = Array.from(new Set(items.map((item) => getTabName(item.type))));
    return [...names.filter((name) => name !== OTHERS_TAB), ...names.filter((name) => name === OTHERS_TAB)];
  }, [items, showTypes]);

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const currentTab = activeTab && tabs.includes(activeTab) ? activeTab : tabs[0] ?? null;
  const visibleItems = useMemo(
    () => (currentTab ? items.filter((item) => getTabName(item.type) === currentTab) : items),
    [items, currentTab]
  );

  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(0);

  const toggleQuestion = useCallback((index: number) => {
    setExpandedQuestion((prev) => (prev === index ? null : index));
  }, []);

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    setExpandedQuestion(0);
  }, []);

  return (
    <section>
      <div className={cn("lg:col-span-8", styles.faqblock)}>
        <h2 id = "faq-heading"
          className={cn("mb-8", styles.mainHeadingFaq , isHome && styles.homeHeadingFaq  )}
        >
          Frequently Asked Questions
        </h2>
        {/* Tabs */}
        {tabs.length > 0 && (
          <div className={cn("flex gap-2 mb-6 flex-wrap", styles.faqtab)}>
            {tabs.map((tab, index) => (
              <button
                id={`faq-button-${index + 1}`}
                disabled={currentTab === tab}
                key={tab}
                type="button"
                className={cn(
                  "px-2 py-2 sm:text-[11px] text-sm font-semibold rounded-lg",
                  styles.tabBtnFaq,
                  {
                    [styles.activeTabFaq]: currentTab === tab,
                    [styles.deactiveTabFaq]: currentTab !== tab,
                  }
                )}
                onClick={() => handleTabChange(tab)}
              >
                {tab.toLowerCase()}
              </button>
            ))}
          </div>
        )}
        {/* Questions */}
        {visibleItems.length > 0 ? (
          <div
            key={currentTab ?? "all"}
            className={cn("md:px-0 sm:!pl-2 px-4 space-y-4", styles.tabPanel)}
          >
            {visibleItems.map((faq, index) => (
              <FaqItem
                key={faq.id ?? index}
                faq={faq}
                index={index}
                isOpen={expandedQuestion === index}
                onToggle={toggleQuestion}
              />
            ))}
          </div>
        ) : (
          <p className="text-gray-600">No FAQs available.</p>
        )}
      </div>
    </section>
  );
};

export default FaqUIWithoutCategory;
